import { rectDeflate, type Rect } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import { paintGlyph } from "../themes/glyphRegistry.js";
import type { Theme } from "../themes/Theme.js";
import { Panel, type PanelOptions } from "./Panel.js";

export interface ExpanderOptions extends PanelOptions {
  text?: string;
  expanded?: boolean;
  /** Height of the whole control when open. Defaults to whatever it was given. */
  expandedHeight?: number;
  /** Seconds for the open or close animation. 0 snaps. */
  animationSeconds?: number;
  onToggle?: (expanded: boolean) => void;
}

const HEADER_PADDING = 8;
const GLYPH_SIZE = 12;
const DEFAULT_ANIMATION_S = 0.16;

/**
 * A header that folds a panel of content away beneath it.
 *
 * The children are ordinary children — this is a `Panel` with a caption bar —
 * so the ones scrolled out of view are clipped rather than removed, and
 * nothing has to be rebuilt when it opens again.
 *
 * Collapsing animates the control's own height rather than the content's, so
 * whatever is laid out below it slides up with it instead of overlapping
 * mid-animation.
 */
export class Expander extends Panel {
  #text: string;
  #expanded: boolean;
  #expandedHeight: number;
  #collapsedHeight = 0;
  /** 0 collapsed, 1 open; anything between is the animation in progress. */
  #openness: number;
  /** Openness when the current animation started. */
  #from = 0;
  /** Desktop frame time the animation started at, or -1 when at rest. */
  #startedAt = -1;

  animationSeconds: number;

  constructor(options: ExpanderOptions = {}) {
    super({ width: 260, height: 160, style: "none", ...options });
    this.#text = options.text ?? "";
    this.#expanded = options.expanded ?? true;
    this.#expandedHeight = options.expandedHeight ?? this.height;
    this.#openness = this.#expanded ? 1 : 0;
    this.animationSeconds = options.animationSeconds ?? DEFAULT_ANIMATION_S;
    if (options.onToggle) this.on("expandedchanged", () => options.onToggle!(this.#expanded));

    this.on("click", (e) => {
      if (e.y <= this.headerHeight()) this.toggle();
    });
    this.on("keydown", (e) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      this.toggle();
      e.handled = true;
    });
    this.tabStop = true;
    if (!this.#expanded) this.#applyHeight();
  }

  get text(): string {
    return this.#text;
  }
  set text(v: string) {
    if (this.#text === v) return;
    this.#text = v;
    this.invalidate();
  }

  get expanded(): boolean {
    return this.#expanded;
  }
  set expanded(v: boolean) {
    if (this.#expanded === v) return;
    // Remember the open height from the last time it was open, so a control
    // resized while open reopens to the size it was left at.
    if (!v) this.#expandedHeight = this.height;
    this.#expanded = v;
    this.emit("expandedchanged");
    this.emit("change");
    const desktop = this.desktop;
    if (this.animationSeconds > 0 && desktop) {
      this.#from = this.#openness;
      this.#startedAt = desktop.frameTime;
      desktop.addAnimator(this);
      desktop.invalidate();
      return;
    }
    this.#openness = v ? 1 : 0;
    this.#applyHeight();
  }

  toggle(): void {
    this.expanded = !this.#expanded;
  }

  /** Height of the clickable caption bar. */
  headerHeight(theme?: Theme): number {
    const t = theme ?? this.desktop?.theme;
    return t ? Math.max(t.metrics.controlHeight - 4, GLYPH_SIZE + HEADER_PADDING) : 26;
  }

  /** Children sit under the header, inside the padding. */
  override get clientRect(): Rect {
    const header = this.headerHeight();
    return rectDeflate(
      { x: 0, y: header, width: this.width, height: Math.max(0, this.height - header) },
      this.padding,
    );
  }

  #applyHeight(): void {
    const header = this.headerHeight();
    const open = Math.max(header, this.#expandedHeight);
    this.#collapsedHeight = header;
    const next = Math.round(this.#collapsedHeight + (open - this.#collapsedHeight) * this.#openness);
    if (next !== this.height) this.setSize(this.width, next);
    this.invalidate();
  }

  /**
   * Move the fold on, if it is moving.
   *
   * Driven from the frame clock rather than a delta, the way `Spinner` reads
   * `frameTime`: registering as an animator buys repaints, and the control
   * works out where it has got to from the time on those.
   */
  #advance(): void {
    if (this.#startedAt < 0) return;
    const desktop = this.desktop;
    if (!desktop) {
      this.#startedAt = -1;
      return;
    }
    const target = this.#expanded ? 1 : 0;
    const elapsed = desktop.frameTime - this.#startedAt;
    const t = this.animationSeconds > 0 ? Math.min(1, elapsed / this.animationSeconds) : 1;
    this.#openness = this.#from + (target - this.#from) * t;
    this.#applyHeight();
    if (t < 1) return;
    this.#openness = target;
    this.#startedAt = -1;
    desktop.removeAnimator(this);
  }

  protected override onEnvironmentChanged(): void {
    // Losing the desktop takes the animator with it; land on the end state
    // rather than freezing part-open with nothing to finish the move.
    if (this.#startedAt < 0) return;
    this.#startedAt = -1;
    this.#openness = this.#expanded ? 1 : 0;
    this.#applyHeight();
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    this.#advance();
    const p = theme.palette;
    const header = this.headerHeight(theme);
    const enabled = this.effectivelyEnabled;

    super.onPaint(rc, theme);

    const bar: Rect = { x: 0, y: 0, width: this.width, height: header };
    if (this.hovered && enabled) rc.fillRect(bar, p.controlBgHover, theme.metrics.controlCornerRadius);

    const box: Rect = {
      x: HEADER_PADDING,
      y: Math.round((header - GLYPH_SIZE) / 2),
      width: GLYPH_SIZE,
      height: GLYPH_SIZE,
    };
    const ink = enabled ? p.text : p.textDisabled;
    // The chevron points down when open and right when shut, which is the way
    // round every tree and disclosure control in Windows draws it.
    paintGlyph(this.#expanded ? "chevron-down" : "chevron-right", { rc, box, ink, weight: 2 });

    rc.drawText(
      this.#text,
      box.x + GLYPH_SIZE + HEADER_PADDING,
      header / 2,
      theme.metrics.captionFont,
      ink,
      { baseline: "middle", maxWidth: Math.max(0, this.width - box.x - GLYPH_SIZE - HEADER_PADDING * 2) },
    );

    if (this.focused && enabled) theme.drawFocusRing(rc, bar);
    if (this.#openness > 0) rc.hLine(0, header, this.width, p.gridLine);
  }
}
